/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, RefreshCcw, FileDown, Users, CheckCircle2, Clock, ScanLine, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { billetterieApi, formatApiError } from "@/lib/api";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
}

const POLL_INTERVAL_MS = 5000;

export default function AdminBilletterieScans() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [data, setData] = useState({ stats: { sold: 0, used: 0, remaining: 0, fill_rate: 0 }, scans: [] });
  const [filter, setFilter] = useState("all"); // all | <ticket_type_id>
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const timerRef = useRef(null);

  const refresh = async () => {
    try {
      const log = await billetterieApi.adminScansLog(id);
      setData(log);
      setLastUpdate(new Date());
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    billetterieApi.adminGet(id).then(setEvent).catch((e) => toast.error(formatApiError(e)));
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!autoRefresh) {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, id]);

  const filtered = filter === "all" ? data.scans : data.scans.filter((s) => (event?.ticket_types || []).find((tt) => tt.name === filter && data.scans.find((sx) => sx.ticket_type_name === filter)) && s.ticket_type_name === filter);

  const exportCsv = () => {
    const rows = [["Nom", "Prénom", "Email", "Téléphone", "Type de billet", "N° Billet", "Prix TTC", "Date & heure de scan", "Scanné par"]];
    for (const s of data.scans) {
      const [prenom, ...rest] = (s.buyer_name || "").split(" ");
      const nom = rest.join(" ");
      rows.push([nom, prenom, s.buyer_email || "", s.buyer_phone || "", s.ticket_type_name, s.code,
                 Number(s.unit_price || 0).toFixed(2).replace(".", ","),
                 fmtDateTime(s.used_at), s.used_by || ""]);
    }
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scans_${event?.slug || id}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Distinct ticket type names from scans (or fall back to all event types)
  const typeFilters = Array.from(new Set([
    ...((event?.ticket_types || []).map((t) => t.name)),
    ...data.scans.map((s) => s.ticket_type_name),
  ]));

  return (
    <div data-testid="admin-bill-scans">
      <header className="mb-6">
        <Link to={`/admin/billetterie/${id}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-[#DCB457]"><ArrowLeft size={11} /> Retour à l'événement</Link>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Contrôle d'entrée</p>
            <h1 className="font-serif-luxe text-3xl md:text-4xl mt-2">Liste des <span className="italic gold-text">arrivées.</span></h1>
            <p className="text-white/55 text-sm mt-1">{event?.name || "—"} · Mise à jour automatique toutes les 5 secondes</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/55">
              {lastUpdate && `Maj ${fmtTime(lastUpdate.toISOString())}`}
            </span>
            <button onClick={() => setAutoRefresh((v) => !v)} data-testid="scan-toggle-auto" className={`text-[11px] uppercase tracking-[0.18em] border px-3 py-1.5 inline-flex items-center gap-1.5 ${autoRefresh ? "border-emerald-400/40 text-emerald-300 bg-emerald-500/8" : "border-white/15 text-white/55"}`}>
              <RefreshCcw size={11} className={autoRefresh ? "animate-spin" : ""} style={{ animationDuration: "3s" }} /> {autoRefresh ? "Auto-refresh ON" : "OFF"}
            </button>
            <button onClick={refresh} className="text-[11px] uppercase tracking-[0.18em] border border-white/15 text-white/65 px-3 py-1.5 hover:bg-white/5 inline-flex items-center gap-1.5">
              <RefreshCcw size={11} /> Rafraîchir
            </button>
            <button onClick={exportCsv} data-testid="scan-export-csv" className="btn-ghost-gold inline-flex items-center gap-2">
              <FileDown size={14} /> Export CSV
            </button>
            <Link to="/admin/billetterie/scan" data-testid="scan-go-scanner" className="btn-gold inline-flex items-center gap-2">
              <ScanLine size={14} /> Scanner
            </Link>
          </div>
        </div>
      </header>

      {/* Stats live */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatTile icon={Users} label="Billets vendus" value={data.stats.sold} />
        <StatTile icon={CheckCircle2} label="Entrées validées" value={data.stats.used} accent />
        <StatTile icon={Clock} label="Pas encore venus" value={data.stats.remaining} />
        <StatTile icon={CheckCircle2} label="Taux de remplissage" value={`${data.stats.fill_rate}%`} />
      </div>

      {/* Filter by type */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setFilter("all")} className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border ${filter === "all" ? "border-[#DCB457] text-[#DCB457] bg-[#DCB457]/8" : "border-white/15 text-white/55"}`}>
          Tous ({data.scans.length})
        </button>
        {typeFilters.map((n) => {
          const count = data.scans.filter((s) => s.ticket_type_name === n).length;
          const tt = (event?.ticket_types || []).find((t) => t.name === n);
          return (
            <button key={n} onClick={() => setFilter(n)} className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border inline-flex items-center gap-1.5 ${filter === n ? "text-[#DCB457] border-[#DCB457] bg-[#DCB457]/8" : "border-white/15 text-white/55 hover:border-[#DCB457]/40"}`}>
              {tt && <span className="w-2 h-2 rounded-full" style={{ background: tt.color }} />} {n} ({count})
            </button>
          );
        })}
      </div>

      {/* Live table */}
      <div className="card-luxe overflow-x-auto" data-testid="scans-table">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-white/55">
            <tr>
              <th className="px-4 py-3 text-left">Heure</th>
              <th className="px-4 py-3 text-left">Nom complet</th>
              <th className="px-4 py-3 text-left">Téléphone</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">N° Billet</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-white/45 italic">
                Aucune entrée pour le moment. Les scans s'afficheront ici en temps réel.
              </td></tr>
            ) : filtered.map((s, i) => {
              const isRecent = s.used_at && (Date.now() - new Date(s.used_at).getTime()) < 10000;
              const tt = (event?.ticket_types || []).find((t) => t.name === s.ticket_type_name);
              return (
                <tr key={s.id} className={`border-t border-[#DCB457]/10 ${isRecent ? "bg-emerald-500/8" : ""}`}>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                    {isRecent && <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />}
                    {fmtTime(s.used_at)}
                  </td>
                  <td className="px-4 py-3 font-medium text-white/90">{s.buyer_name || "—"}</td>
                  <td className="px-4 py-3 text-white/65 text-xs">
                    {s.buyer_phone ? (
                      <a href={`tel:${s.buyer_phone}`} className="inline-flex items-center gap-1 hover:text-[#DCB457]">
                        <Phone size={10} /> {s.buyer_phone}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-white/55 text-xs">
                    {s.buyer_email ? (
                      <a href={`mailto:${s.buyer_email}`} className="inline-flex items-center gap-1 hover:text-[#DCB457]">
                        <Mail size={10} /> {s.buyer_email}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      {tt && <span className="w-2 h-2 rounded-full" style={{ background: tt.color }} />}
                      {s.ticket_type_name}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#DCB457]">{s.code}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-white/45 mt-4 italic">
        Les nouvelles entrées apparaissent en haut avec un point vert pendant 10 secondes. Cette page est conçue pour rester ouverte sur un écran de contrôle pendant l'événement.
      </p>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className={`card-luxe p-4 ${accent ? "border-emerald-400/40" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="eyebrow">{label}</p>
        <Icon size={14} strokeWidth={1.3} className={accent ? "text-emerald-300" : "text-[#DCB457]"} />
      </div>
      <p className="font-serif-luxe text-2xl gold-text">{value}</p>
    </div>
  );
}
